import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../api/axios";
import "../styles/scanner.css";

type Handler = (code: string) => void;

interface Ctx {
  register: (h: Handler) => () => void;
}

const ScannerCtx = createContext<Ctx | null>(null);

const ON_KEY = "scanner.on";
const QR_URL = "/qrcode.js";

let qrLoading: Promise<any> | null = null;
const loadQr = () => {
  const w = window as any;
  if (w.qrcode) return Promise.resolve(w.qrcode);
  if (!qrLoading) {
    qrLoading = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = QR_URL;
      s.onload = () => resolve(w.qrcode);
      s.onerror = () => { qrLoading = null; reject(new Error("qr")); };
      document.head.appendChild(s);
    });
  }
  return qrLoading;
};

/**
 * Страница (или окно) подписывается на коды с телефона: код получает последний подписавшийся,
 * то есть открытое поверх окно (например, приход) важнее страницы под ним.
 */
export function useScanHandler(handler: Handler, enabled = true) {
  const ctx = useContext(ScannerCtx);
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!ctx || !enabled) return;
    return ctx.register((c) => ref.current(c));
  }, [ctx, enabled]);
}

export function ScannerProvider({ children }: { children: React.ReactNode }) {
  const handlers = useRef<Handler[]>([]);
  const [open, setOpen] = useState(false);
  const [on, setOn] = useState(() => { try { return localStorage.getItem(ON_KEY) === "1"; } catch { return false; } });
  const [code, setCode] = useState<string | null>(null);
  const [phone, setPhone] = useState(false);
  const [lastScan, setLastScan] = useState<{ value: string; at: number } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [qrSvg, setQrSvg] = useState("");
  const [err, setErr] = useState("");

  const register = useCallback((h: Handler) => {
    handlers.current.push(h);
    return () => { handlers.current = handlers.current.filter((x) => x !== h); };
  }, []);

  const deliver = useCallback((value: string) => {
    const h = handlers.current[handlers.current.length - 1];
    setLastScan({ value, at: Date.now() });
    if (h) h(value);
    else setToast(`Код ${value} получен, но на этой странице нет поля для сканера`);
  }, []);

  // сессия подключения
  const ensureSession = useCallback(async (fresh = false) => {
    try {
      const r = await api.post("/scanner/session", { fresh });
      setCode(r.data.code);
      setErr("");
      return r.data.code as string;
    } catch {
      setErr("Не удалось подключить сканер");
      return null;
    }
  }, []);

  // долгий опрос новых кодов
  useEffect(() => {
    if (!on) return;
    let stop = false;
    let after = 0;
    let cur: string | null = null;
    (async () => {
      cur = await ensureSession();
      // первый запрос только запоминает номер последнего кода: всё, что пришло до включения, не доставляем
      let first = true;
      while (!stop) {
        if (!cur) { await new Promise((r) => setTimeout(r, 5000)); if (stop) return; cur = await ensureSession(); continue; }
        try {
          const r = await api.get("/scanner/poll", { params: { code: cur, after: first ? -1 : after }, timeout: 30000 });
          if (stop) return;
          setPhone(!!r.data.phone);
          if (first) { after = r.data.seq; first = false; continue; }
          for (const it of r.data.items as { seq: number; value: string }[]) { after = Math.max(after, it.seq); deliver(it.value); }
        } catch (e: any) {
          if (stop) return;
          if (e?.response?.status === 404) { cur = await ensureSession(true); after = 0; first = true; }
          else await new Promise((r) => setTimeout(r, 3000));
        }
      }
    })();
    return () => { stop = true; };
  }, [on, ensureSession, deliver]);

  // QR рисуем библиотекой с нашего сервера
  useEffect(() => {
    if (!open || !code) return;
    let cancelled = false;
    loadQr().then((qrcode) => {
      if (cancelled) return;
      const qr = qrcode(0, "M");
      // в приложении для ПК адрес вида app://crm: телефону нужен настоящий адрес сайта
      const base = window.location.protocol.startsWith("http") ? window.location.origin : "https://crm.keyoff.kz";
      qr.addData(`${base}/scan/${code}`);
      qr.make();
      setQrSvg(qr.createSvgTag({ cellSize: 6, margin: 2, scalable: true }));
    }).catch(() => setErr("Не удалось построить QR-код"));
    return () => { cancelled = true; };
  }, [open, code]);

  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 3500); return () => clearTimeout(t); }, [toast]);

  const turnOn = () => { setOn(true); try { localStorage.setItem(ON_KEY, "1"); } catch { /* */ } setOpen(true); };
  const turnOff = () => { setOn(false); setPhone(false); setOpen(false); try { localStorage.setItem(ON_KEY, "0"); } catch { /* */ } };
  const recreate = async () => { setQrSvg(""); await ensureSession(true); };

  return (
    <ScannerCtx.Provider value={{ register }}>
      {children}
      <div className="scn-dock">
        {open && (
          <div className="scn-pop" role="dialog" aria-label="Сканер с телефона">
            <div className="scn-pop-h">
              <b>📱 Телефон как сканер</b>
              <button className="scn-x" onClick={() => setOpen(false)} aria-label="Закрыть">×</button>
            </div>
            {!on ? (
              <>
                <p className="scn-p">Телефон сканирует штрихкод, а код сразу появляется в открытом поле CRM: в кассе, ценниках, приходе и карточке товара.</p>
                <button className="scn-btn" onClick={turnOn}>Подключить телефон</button>
              </>
            ) : (
              <>
                <div className={`scn-state ${phone ? "ok" : ""}`}>{phone ? "● Телефон подключён" : "○ Ждём телефон"}</div>
                {qrSvg ? <div className="scn-qr" dangerouslySetInnerHTML={{ __html: qrSvg }} /> : <div className="scn-qr scn-qr-load">{err || "Готовим код…"}</div>}
                <p className="scn-p">Наведите камеру телефона на QR-код и откройте ссылку. Дальше просто сканируйте штрихкоды: код попадёт в то поле, где стоит курсор.</p>
                {lastScan && <div className="scn-last">Последний код: <b>{lastScan.value}</b></div>}
                <div className="scn-row">
                  <button className="scn-btn ghost" onClick={recreate}>Новый QR</button>
                  <button className="scn-btn ghost" onClick={turnOff}>Отключить</button>
                </div>
              </>
            )}
          </div>
        )}
        {toast && <div className="scn-toast">{toast}</div>}
        <button className={`scn-fab ${on && phone ? "ok" : ""}`} onClick={() => setOpen((o) => !o)} title="Сканер штрихкодов с телефона">
          📱<span>{on ? (phone ? "Сканер подключён" : "Сканер: ждём телефон") : "Сканер"}</span>
        </button>
      </div>
    </ScannerCtx.Provider>
  );
}
